import React, { useMemo, useState } from "react";
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import moment from "moment";
import {
  clampImporte,
  esDocumentoAdelanto,
  esPagoNoProveedor,
  estadoComprobanteOp,
  etiquetaComprobanteCorta,
  formatCuit,
  formatMoneda,
  idDocumentoPendiente,
  importeEsValido,
  limitesImporteInput,
  tooltipTipoComprobante,
} from "./utils";

const stickyAccionSx = (isHeader) => ({
  position: "sticky",
  right: 0,
  zIndex: isHeader ? 4 : 3,
  width: 52,
  minWidth: 52,
  maxWidth: 52,
  px: 0.25,
  // Fondos opacos: sin esto el sticky se ve “vacío” al scrollear.
  bgcolor: isHeader ? "#2471A3" : "#fff",
  backgroundClip: "padding-box",
  boxShadow: "-6px 0 10px rgba(0,0,0,0.08)",
  borderLeft: isHeader
    ? "1px solid rgba(255,255,255,0.25)"
    : "1px solid #e0e0e0",
});

const claveProveedorGrupo = (row) => {
  if (esPagoNoProveedor(row)) {
    return `pnp:${row.id_pago_no_proveedor ?? row.razon_social ?? "pnp"}`;
  }
  const cuit = String(row.nro_cuit || row.cuit || "").replace(/\D/g, "");
  return cuit || `prov:${row.razon_social || "sin-cuit"}`;
};

const calcularImporteFila = (row, importesAPagar) => {
  const esAdelanto = esDocumentoAdelanto(row);
  const esPnp = esPagoNoProveedor(row);
  const id = idDocumentoPendiente(row);
  const tope =
    row.importe_maximo ??
    row.saldo_disponible ??
    Math.abs(Number(row.saldo_pendiente ?? row.monto) || 0);
  const saldoTope = Number(row.saldo_pendiente ?? row.monto);
  const saldoValido = Number.isFinite(saldoTope)
    ? saldoTope
    : esAdelanto
      ? -Math.abs(Number(tope) || 0)
      : Number(tope) || 0;
  const valorImporte =
    (id != null && importesAPagar[id] != null ? importesAPagar[id] : null) ??
    row.importe_a_pagar ??
    saldoValido;
  const importeNum = esPnp
    ? Number(row.saldo_pendiente ?? row.monto ?? valorImporte) || 0
    : Number(valorImporte) || 0;

  return { esAdelanto, esPnp, id, tope, saldoValido, valorImporte, importeNum };
};

const agruparFilasPorProveedor = (filas) => {
  const map = new Map();
  filas.forEach((row) => {
    const key = claveProveedorGrupo(row);
    if (!map.has(key)) {
      map.set(key, {
        key,
        razon_social: row.razon_social,
        cuit: row.nro_cuit || row.cuit,
        filas: [],
      });
    }
    map.get(key).filas.push(row);
  });
  return Array.from(map.values());
};

/**
 * Tabla de comprobantes incluidos en la OP.
 * Con `editable` + handlers, la columna "Importe a pagar" permite pago parcial.
 * Con `onQuitarComprobante`, columna fija a la derecha con tachito para quitar.
 */
const ComprobantesIncluidosTable = ({
  filas,
  cantidad,
  editable = false,
  importesAPagar = {},
  onImportesAPagarChange,
  onQuitarComprobante,
  puedeQuitarFila,
  quitarDisabled = false,
}) => {
  const puedeEditar =
    Boolean(editable) && typeof onImportesAPagarChange === "function";
  const puedeQuitar = typeof onQuitarComprobante === "function";
  const [pendienteQuitar, setPendienteQuitar] = useState(null);

  const gruposProveedor = useMemo(
    () => agruparFilasPorProveedor(filas),
    [filas]
  );

  const headers = [
    "Proveedor",
    "CUIT",
    "Comprobante",
    "Fecha factura",
    "Vencimiento",
    "Total factura",
    "Importe a pagar",
    "Tope",
    "Estado",
  ];

  const confirmarQuitar = () => {
    if (!pendienteQuitar) return;
    onQuitarComprobante(pendienteQuitar.id, pendienteQuitar.row);
    setPendienteQuitar(null);
  };

  const renderFilaComprobante = (row, esPrimeraDelGrupo = false) => {
    const estado = estadoComprobanteOp(row);
    const { esAdelanto, esPnp, id, tope, saldoValido, valorImporte } =
      calcularImporteFila(row, importesAPagar);
    const invalido =
      puedeEditar &&
      !esPnp &&
      !importeEsValido(valorImporte, saldoValido, row);
    const bloqueadoQuitar =
      quitarDisabled ||
      Number(cantidad) <= 1 ||
      !id ||
      (typeof puedeQuitarFila === "function" && !puedeQuitarFila(row));

    const tooltipQuitar = !id
      ? "No se puede quitar"
      : Number(cantidad) <= 1 || quitarDisabled
        ? "Debe quedar al menos un comprobante"
        : typeof puedeQuitarFila === "function" && !puedeQuitarFila(row)
          ? "Debe quedar al menos un comprobante de este proveedor"
          : "Quitar de esta orden";

    return (
      <TableRow
        key={
          id ||
          row.idcomprobante ||
          row.id_pago_no_proveedor ||
          row.nro_comprobante
        }
        hover
        sx={{
          "&:hover .celda-accion-quitar": {
            bgcolor: "#fafafa",
          },
        }}
      >
        <TableCell align="left" sx={{ maxWidth: 220 }}>
          {esPrimeraDelGrupo ? (
            <Typography
              variant="body2"
              sx={{
                fontSize: 12,
                lineHeight: 1.3,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={row.razon_social || ""}
            >
              {row.razon_social || "—"}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
          {esPrimeraDelGrupo ? formatCuit(row.nro_cuit || row.cuit) : null}
        </TableCell>
        <TableCell align="center">
          <Stack
            direction="row"
            spacing={0.5}
            justifyContent="center"
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <Tooltip title={tooltipTipoComprobante(row) || ""}>
              <span>{etiquetaComprobanteCorta(row)}</span>
            </Tooltip>
                        {esPnp ? (
                          <Chip size="small" color="secondary" label="Pago varios" />
                        ) : null}
          </Stack>
        </TableCell>
        <TableCell align="center">
          {row.fecha_factura
            ? moment(row.fecha_factura).format("DD/MM/YYYY")
            : "—"}
        </TableCell>
        <TableCell align="center">
          {row.fecha_vencimiento
            ? moment(row.fecha_vencimiento).format("DD/MM/YYYY")
            : "—"}
        </TableCell>
        <TableCell align="center">
          {formatMoneda(
            esAdelanto
              ? row.monto_anticipo ?? row.total ?? row.total_comprobante
              : row.total ?? row.total_comprobante
          )}
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 600 }}>
          {puedeEditar && id && !esPnp ? (
            <TextField
              size="small"
              type="number"
              value={valorImporte}
              error={invalido}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) =>
                onImportesAPagarChange((prev) => ({
                  ...prev,
                  [id]: e.target.value,
                }))
              }
              onBlur={() =>
                onImportesAPagarChange((prev) => ({
                  ...prev,
                  [id]: clampImporte(prev[id] ?? valorImporte, saldoValido, row),
                }))
              }
              inputProps={limitesImporteInput(saldoValido, row)}
              sx={{
                width: 120,
                "& .MuiInputBase-root": {
                  height: 28,
                  fontSize: 13,
                },
                "& .MuiInputBase-input": {
                  py: 0,
                  px: 1,
                  textAlign: "right",
                },
              }}
            />
          ) : (
            formatMoneda(
              esPnp
                ? row.saldo_pendiente ?? row.monto ?? valorImporte
                : valorImporte
            )
          )}
        </TableCell>
        <TableCell align="center">
          {formatMoneda(esAdelanto ? -Math.abs(tope) : tope)}
        </TableCell>
        <TableCell align="center">
          <Chip label={estado.label} color={estado.color} size="small" />
        </TableCell>
        {puedeQuitar ? (
          <TableCell
            align="center"
            className="celda-accion-quitar"
            sx={stickyAccionSx(false)}
          >
            <Tooltip title={tooltipQuitar}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={bloqueadoQuitar}
                  aria-label="Quitar comprobante"
                  onClick={() =>
                    setPendienteQuitar({
                      id,
                      row,
                      label: etiquetaComprobanteCorta(row),
                    })
                  }
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </TableCell>
        ) : null}
      </TableRow>
    );
  };

  const renderSubtotalProveedor = (grupo) => {
    const total = grupo.filas.reduce(
      (acc, row) => acc + calcularImporteFila(row, importesAPagar).importeNum,
      0
    );
    const cantidadComprobantes = grupo.filas.length;

    return (
      <TableRow
        key={`subtotal-${grupo.key}`}
        sx={{
          bgcolor: "#eef4f9",
          borderTop: "2px solid #c5d9e8",
          "& td": { borderBottom: "2px solid #c5d9e8" },
        }}
      >
        <TableCell colSpan={6} align="right" sx={{ py: 1 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ fontSize: 12, color: "#1a5276" }}
          >
            Total a pagar
            {cantidadComprobantes > 1
              ? ` (${cantidadComprobantes} comprobantes)`
              : ""}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={{ fontWeight: 700, color: "#1a5276" }}>
          {formatMoneda(total)}
        </TableCell>
        <TableCell />
        <TableCell />
        {puedeQuitar ? (
          <TableCell
            sx={{
              ...stickyAccionSx(false),
              bgcolor: "#eef4f9",
            }}
          />
        ) : null}
      </TableRow>
    );
  };

  return (
    <>
      <Paper elevation={1} sx={{ p: 2, backgroundColor: "#fff" }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Comprobantes incluidos
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1.5 }}
        >
          {cantidad} comprobante{cantidad !== 1 ? "s" : ""} seleccionado
          {cantidad !== 1 ? "s" : ""}
          {puedeEditar
            ? " · podés ajustar el importe a pagar de cada uno"
            : ""}
          {puedeQuitar ? " · usá el tachito para sacarlo de esta orden" : ""}
        </Typography>
        <TableContainer
          sx={{
            overflowX: "auto",
            maxWidth: "100%",
            // Necesario para que position:sticky funcione en celdas de tabla.
            "& .MuiTable-root": {
              borderCollapse: "separate",
              borderSpacing: 0,
            },
          }}
        >
          <Table size="small" sx={{ minWidth: puedeQuitar ? 1180 : 1060 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#2471A3" }}>
                {headers.map((h) => (
                  <TableCell
                    key={h}
                    align="center"
                    sx={{
                      color: "#fff",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
                {puedeQuitar ? (
                  <TableCell align="center" sx={stickyAccionSx(true)}>
                    <Tooltip title="Quitar comprobante">
                      <DeleteOutlineIcon
                        sx={{ fontSize: 18, color: "#fff", opacity: 0.95 }}
                      />
                    </Tooltip>
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {gruposProveedor.map((grupo, idxGrupo) => (
                <React.Fragment key={grupo.key}>
                  {grupo.filas.map((row, idxFila) =>
                    renderFilaComprobante(row, idxFila === 0)
                  )}
                  {grupo.filas.length > 1
                    ? renderSubtotalProveedor(grupo)
                    : null}
                  {idxGrupo < gruposProveedor.length - 1 ? (
                    <TableRow
                      key={`sep-${grupo.key}`}
                      sx={{ height: 8, bgcolor: "#fafafa" }}
                    >
                      <TableCell
                        colSpan={headers.length + (puedeQuitar ? 1 : 0)}
                        sx={{ p: 0, border: 0 }}
                      />
                    </TableRow>
                  ) : null}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={Boolean(pendienteQuitar)}
        onClose={() => setPendienteQuitar(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Quitar comprobante</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendienteQuitar?.label
              ? `¿Sacar ${pendienteQuitar.label} de esta orden de pago?`
              : "¿Sacar este comprobante de la orden de pago?"}{" "}
            Se recalculan retenciones y el total a pagar.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendienteQuitar(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarQuitar}>
            Quitar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ComprobantesIncluidosTable;
